import { Machine } from "../GameComponents/Machine";
import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Symbols } from "../GameComponents/Symbols";
import { TimelineElement } from "../Components/Elements/Graphics/TimelineElement";
import { AppContext } from "../../Common/AppContext";
import { DataManager } from "../Managers/DataManager";
import { GameData } from "../Channels/Common/Datas/GameData";
import { PlayList } from "../Components/Sounds/PlayList";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { Reel2 } from "../GameComponents/Reel2";
import { Configuration } from "../Components/Configuration";
import { Reel } from "../GameComponents/Reel";
import { ReelExtended } from "./ReelExtended";
import { IReelTeasingAttributes, ReelTeasingActions, TeasingComponent } from "./TeasingComponent";
import { TeasingBonus } from "./TeasingBonus";
import { ModuleManager } from "../Managers/ModuleManager";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";

@engineComponent(ComponentTypes.DisplayObject)
export class MachineExtended extends Machine {
    private _allowTeasing: boolean;
    private ledStripeTeasing?: string;
    private ledStripeNormal?: string;

    private teaseOnDelay: number = 1000;

    private _forceStop: boolean = false;

    private _expandingWildHideSymbolsDelay: number;
    private _reelsContainerOver: PIXI.Container;

    private _randomWildExpectation: number;
    private _wildExpectationDelay: number;
    private _isWildExpectation: boolean;

    get forceStop(): boolean {
        return this._forceStop;
    }

    configurate(node: Element): void {
        super.configurate(node);

        if (node.hasAttribute("RandomWildExpectation")) {
            this._randomWildExpectation = parseInt(node.getAttribute("RandomWildExpectation"));
        }

        if (node.hasAttribute("WildExpectationDelay")) {
            this._wildExpectationDelay = parseInt(node.getAttribute("WildExpectationDelay"));
        }

        if (node.hasAttribute("ExpandingWildHideSymbolsDelay")) {
            this._expandingWildHideSymbolsDelay = parseInt(node.getAttribute("ExpandingWildHideSymbolsDelay"));
        }
    }

    initialize(symbols: Symbols, reels: Array<Array<number>>): void {
        if (this.parent && this.parent.children) {
            this.parent.children.forEach((child) => {
                if (child instanceof ContainerElement && child.name == "ReelsOver") {
                    this._reelsContainerOver = child;
                }
            });
        }
        if (!this._reelsContainerOver) {
            this._reelsContainerOver = this._reelsContainer;
        }

        let ledConfig: Element = Configuration.Configurations["TeasingLedStripeConfiguration"];
        if (ledConfig) {
            this.ledStripeTeasing = ledConfig.hasAttribute("OnEnter") ? ledConfig.getAttribute("OnEnter") : null;
            this.ledStripeNormal = ledConfig.hasAttribute("OnExit") ? ledConfig.getAttribute("OnExit") : null;
        }

        super.initialize(symbols, reels);
    }

    public spinStart(longRotation?: boolean): void {
        super.spinStart(longRotation);
        if (this.elementInstance["teasing"] != null) {
            this.elementInstance["teasing"].forEach((teasingComponent: TeasingComponent) => {
                teasingComponent.spinStart();
            }, this);
        }
    }

    spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        this._forceStop = force;

        if (fromServer) {
            this._stoppedFromServer = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            if (this.verifyMinGameRoundDurationConditions()) {
                this._legalStopTimer = setTimeout(
                    () => this.spinStop(null, false, false, true),
                    this.getLegalRemainingSpinDuration(),
                );
            } else {
                this._stoppedFromLegal = true;
            }
        } else if (legal) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
        } else {
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (force) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (DEBUG) {
            this.log("spinStop " + force);
        }

        if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
            if (
                !ModuleManager.freespinsActive() &&
                !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")
            ) {
                DataManager.updateDataElement(GameData.DataTotalWin, "0");
                DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            }

            let rta: Array<IReelTeasingAttributes>;
            if (this.elementInstance["teasing"] != null) {
                this.elementInstance["teasing"].forEach((teasingComponent: TeasingComponent) => {
                    let reelTeasingAttributes = teasingComponent.checkOnInit(
                        { force: force, stoppedSymbols: this._stoppedSymbolsArray },
                        ModuleManager.freespinsActive(),
                    );
                    if (teasingComponent.teasingEnabled) {
                        rta = reelTeasingAttributes;
                    }

                    for (let i: number = 0; i < this._reelCount; i++) {
                        let foundedIndex = teasingComponent.hasReelExpandingWild(i);
                        if (foundedIndex >= 0) {
                            this._reels[i].hideReel(
                                teasingComponent.getExpandingWildIntroDelay() +
                                    this._expandingWildHideSymbolsDelay +
                                    teasingComponent.getExpandingWildStartDelay() * foundedIndex +
                                    teasingComponent.getExpandingAdditionReelDelay(foundedIndex),
                            );
                        }
                    }
                }, this);
            }

            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        if (this._stoppedSymbolsArray.length !== 0) {
                            this._reels[i].setData(this._stoppedSymbolsArray[i]);

                            if (typeof rta !== "undefined") {
                                if (rta[i] && rta[i].action == ReelTeasingActions.TEASE) {
                                    this._reels[i].spinTease([], rta[i].stopDelay);
                                } else {
                                    this._reels[i].spinStop(force, rta[i].stopDelay);
                                }
                            } else if (this._isWildExpectation) {
                                this._reels[i].spinStop(force, this._wildExpectationDelay + i * this._stopDelay);
                            } else {
                                this._reels[i].spinStop(force);
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no stopped symbols");
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }
        }
    }

    private getReel(reelIndex: number): ReelExtended {
        return <ReelExtended>this._reels[reelIndex];
    }

    public getReelCount(): number {
        return this._reelCount;
    }

    onReelStopped(reelIndex: number): void {
        let currentReel = this.getReel(reelIndex);

        let hasExpandingWildAnimation = false;
        if (this.elementInstance["teasing"] != null) {
            this.elementInstance["teasing"].forEach((teasingComponent: TeasingComponent) => {
                let foundedIndex = teasingComponent.hasReelExpandingWild(reelIndex);
                if (foundedIndex >= 0) {
                    hasExpandingWildAnimation = true;
                }
            }, this);
        }

        if (!hasExpandingWildAnimation) {
            PlayList.play("SND_BaseRotationEnd" + reelIndex);
        }

        this._stoppedReelIndexes++;
        this.actualReelStopped = reelIndex;
        currentReel.visible = false;
        this.emit(Machine.ReelStopped, this._allowTeasing);

        if (this._stoppedReelIndexes === this._reelCount || this.respin != null) {
            PlayList.stop("SND_BaseRotation", 10);
            PlayList.stop("SND_BaseRotationStart", 10);
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stopTimer.stop();
            this._reelsContainer.visible = false;

            if (DEBUG) {
                const old = AppContext.gameConfiguration.MinGameRoundDuration;
                const current = AppContext.gameConfiguration.GameRoundMinSpan;
                const minDuration = current > 0 ? current : old > 0 ? old : 0;
                const spinDuration = Date.now() - this._startSpinTime;

                if (spinDuration > minDuration || !this.verifyMinGameRoundDurationConditions()) {
                    this.log("Spin time duration: " + spinDuration + " ms");
                } else {
                    console.log("%cError spin time duration:" + spinDuration + " ms", "color: #ff9900");
                }
            }

            this.emit(Machine.MachineStopped);
        }

        if (this.elementInstance["teasing"] != null) {
            this.elementInstance["teasing"].forEach((teasingComponent: TeasingBonus) => {
                teasingComponent.reelStopped(reelIndex);
            });
        }

        if (reelIndex + 1 === this._reelCount || this._forceStop) {
            /*
            if (this.elementInstance['teasing'] != null) {
                this.elementInstance['teasing'].forEach((teasingComponent: TeasingBonus) => {
                    teasingComponent.finishTeasing(reelIndex);
                });
            }*/
        } else {
            if (this.elementInstance["teasing"] != null) {
                this.elementInstance["teasing"].forEach((teasingComponent: TeasingBonus) => {
                    let tease: boolean = teasingComponent.animateTeasing(reelIndex);

                    for (let i = reelIndex + 1; i < this._reelCount; i++) {
                        if (this._reels[i]) {
                            this.getReel(i).setTeaseReel(teasingComponent.isTeasing(reelIndex));
                        }
                    }

                    if (this._reels[reelIndex + 1]) {
                        this.getReel(reelIndex + 1).setTeasing(tease, tease ? this.teaseOnDelay : 0);
                    }
                });
            }
        }
    }

    onSpinPostRoll(reelIndex: number): void {
        if (reelIndex + 1 === this._reelCount || this._forceStop) {
            if (this.elementInstance["teasing"] != null) {
                this.elementInstance["teasing"].forEach((teasingComponent: TeasingBonus) => {
                    teasingComponent.finishTeasing(reelIndex);
                });
            }
        }
    }

    protected createReels(reels: Array<Array<number>>): void {
        for (let i: number = 0; i < this._reelCount; i++) {
            this._reels.push(new ReelExtended());
        }

        for (let i: number = 0; i < this._reelCount; i++) {
            const reel = this._reels[i];

            if (this._reelConfiguration != null) {
                let configs: Array<string> = this._reelConfiguration.split(";");
                for (let j: number = 0; j < configs.length; j++) {
                    reel.configure(Configuration.Configurations[configs[j]]);
                }
            }
            reel.visible = false;
            //reel.initialize(i, this._symbols, reels[i], this._reelX[i], this._reelY[i], this._visibleSymbolCount[i], 1, 1, this._symbolWidth, this._symbolHeight, this._startDelay * i, this._stopDelay * i);
            reel.initialize(
                i > 0 ? this._reels[i - 1] : null,
                i < this._reelCount - 1 ? this._reels[i + 1] : null,
                i,
                this._symbols,
                reels[i],
                this._reelX[i],
                this._reelY[i],
                this._visibleSymbolCount[i],
                1,
                1,
                this._symbolWidth,
                this._symbolHeight,
                this._startDelay * i,
                this._stopDelay * i,
            );
            reel.on(Reel.ReelStopped, this.onReelStopped.bind(this));
            reel.on(Reel.ReelStopping, this.onReelStopping.bind(this));
            reel.on(ReelExtended.OnSpinPostRoll, this.onSpinPostRoll.bind(this));
            this._reelsContainer.addChild(reel);

            this._reelsContainerOver.addChild((<ReelExtended>reel).bonusContainer);
        }
        this._reelsContainer.visible = false;
    }

    public setWildExpectation(isWildExpectation: boolean): void {
        this._isWildExpectation = isWildExpectation;
    }

    public getRandomWildExpectation(): number {
        return this._randomWildExpectation;
    }

    public onReelStopping(reelIndex: number): void {
        /*
        if (this.elementInstance['teasing'] != null) {
            this.elementInstance['teasing'].forEach((teasingComponent: TeasingBonus) => {
                let tease: boolean = teasingComponent.animateTeasing(reelIndex);
                if(this._reels[reelIndex+1]) {
                    this.getReel(reelIndex+1).setTeasing(tease, tease ? this.teaseOnDelay : 0);
                }
            });
        }

        if (this._stoppedReelIndexes + 1 === this._reelCount || this._forceStop) {
            if (this.elementInstance['teasing'] != null) {
                this.elementInstance['teasing'].forEach((teasingComponent: TeasingBonus) => {
                    teasingComponent.finishTeasing(reelIndex);

                });
            }
        }*/
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MachineExtended.ts
 **/